import { useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListAssigneesQuery,
  useUpsertAssigneeMutation,
  useDeleteAssigneeMutation,
} from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { Assignee } from '../types/models';

const COLOR_PALETTE = [
  '#1976d2',
  '#2e7d32',
  '#ed6c02',
  '#d32f2f',
  '#9c27b0',
  '#0288d1',
  '#f5a623',
  '#5b6cff',
  '#00897b',
  '#c2185b',
];

interface FormData {
  name: string;
  email?: string;
  color: string;
}

export default function AssigneesPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const dispatch = useAppDispatch();
  const { data: list = [], isLoading } = useListAssigneesQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );
  const [upsert] = useUpsertAssigneeMutation();
  const [remove] = useDeleteAssigneeMutation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Assignee | null>(null);
  const [toDelete, setToDelete] = useState<Assignee | null>(null);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } =
    useForm<FormData>({
      defaultValues: { name: '', email: '', color: COLOR_PALETTE[0] },
    });

  if (!projectId) {
    return (
      <>
        <PageHeader title="Assignees" />
        <Alert severity="info">Select a project to manage assignees.</Alert>
      </>
    );
  }

  const color = watch('color');

  const openCreate = () => {
    setEditing(null);
    reset({ name: '', email: '', color: COLOR_PALETTE[list.length % COLOR_PALETTE.length] });
    setOpen(true);
  };

  const openEdit = (a: Assignee) => {
    setEditing(a);
    reset({ name: a.name, email: a.email ?? '', color: a.color });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await upsert({
        uid,
        projectId,
        id: editing?.id,
        ...data,
      }).unwrap();
      dispatch(showToast({ message: editing ? 'Assignee updated' : 'Assignee created', severity: 'success' }));
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Assignee deleted', severity: 'success' }));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  return (
    <>
      <PageHeader
        title="Assignees"
        subtitle="Team members within this project."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New assignee
          </Button>
        }
      />

      {!isLoading && list.length === 0 ? (
        <EmptyState
          title="No assignees"
          description="Add team members so tasks can be assigned."
          action={
            <Button variant="contained" onClick={openCreate} startIcon={<AddIcon />}>
              Add assignee
            </Button>
          }
        />
      ) : (
        <Grid container spacing={2}>
          {list.map((a) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={a.id}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: a.color, width: 48, height: 48 }}>
                    {a.name[0]?.toUpperCase()}
                  </Avatar>
                  <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>
                      {a.name}
                    </Typography>
                    {a.email && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {a.email}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => openEdit(a)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setToDelete(a)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit assignee' : 'New assignee'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Name"
                autoFocus
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name', { required: 'Required' })}
              />
              <TextField label="Email" {...register('email')} />
              <Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  Profile color
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {COLOR_PALETTE.map((c) => (
                    <IconButton
                      key={c}
                      size="small"
                      onClick={() => setValue('color', c)}
                      sx={{
                        bgcolor: c,
                        width: 28,
                        height: 28,
                        border: color === c ? '3px solid #fff' : 'none',
                        outline: color === c ? `2px solid ${c}` : 'none',
                        '&:hover': { bgcolor: c, opacity: 0.85 },
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete assignee?"
        message={`"${toDelete?.name}" will be removed.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
